//
// styles.ts — inline style objects for the panel UI.
//
// Each constant corresponds to one DOM node in the panel hierarchy,
// and is ready to pass as a `style={...}` prop on a Preact element.
// We use inline styles rather than a stylesheet because the embed
// runs inside the customer's page, and external CSS risks class-name
// collisions with their app.
//
// Colours and font stacks are repeated per style object rather than
// extracted into a token layer — the panel is small and self-contained
// enough that a design-system indirection would cost more than it saves.
//
import type { JSX } from 'preact';

//
// int32 max — the highest z-index the browser accepts. Keeps the
// panel and launcher above any modal, dropdown, or toast the host app
// might render.
//
export const MAXZ = '2147483647';

//
// Tone → colour map used by the status line. Kept as an `as const`
// object so StatusTone below is narrowed to the literal union.
//
export const statusTone = {
  info: '#d1d5db',
  error: '#f87171',
  success: '#86efac',
} as const;

export type StatusTone = keyof typeof statusTone;

//
// The floating launcher button in the top-right corner.
//
export const launcherButtonStyle: JSX.CSSProperties = {
  position: 'fixed',
  top: '12px',
  right: '12px',
  zIndex: MAXZ,
  padding: '8px 14px',
  background: '#1f2937',
  color: '#fff',
  border: '1px solid #374151',
  borderRadius: '6px',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  fontSize: '13px',
  fontWeight: '600',
  cursor: 'pointer',
  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
};

//
// Root container of the expanded panel. Wide enough to host a
// horizontal timeline with four track rows. Laid out as a flex
// column so the header and footer can pin while the body
// scrolls independently (see panelBodyStyle / panelFooterStyle).
//
export const panelRootStyle: JSX.CSSProperties = {
  position: 'fixed',
  top: '12px',
  right: '12px',
  width: '720px',
  maxHeight: 'calc(100vh - 24px)',
  overflowX: 'hidden',
  zIndex: MAXZ,
  background: '#111827',
  color: '#f3f4f6',
  border: '1px solid #374151',
  borderRadius: '8px',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  fontSize: '13px',
  boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
  padding: '14px 14px 12px',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
};

//
// Middle, scrollable region of the panel. Holds the map detection
// status, initial-view row, timeline, add-action row, optional
// action editor, overlay/layers details, and fade controls. Uses
// `flex: 1` so it expands to fill the available vertical space
// between the pinned header and footer, and `minHeight: 0` so flex
// allows it to shrink below its content height — that's what
// triggers the internal scrollbar rather than pushing the footer
// out of view.
//
export const panelBodyStyle: JSX.CSSProperties = {
  flex: '1 1 auto',
  minHeight: '0',
  overflowY: 'auto',
  overflowX: 'hidden',
};

//
// Pinned footer with the demo-load buttons, Render, progress bar
// and status line. Separated from the body by a thin top border
// so the scroll edge reads visually as "end of scrollable area".
// Slightly darker background than the body — same shade as the
// timeline / editor panels — so header and footer read as a
// distinct chrome band bracketing the content.
//
export const panelFooterStyle: JSX.CSSProperties = {
  flex: '0 0 auto',
  marginTop: '10px',
  paddingTop: '10px',
  borderTop: '1px solid #1f2937',
  background: '#0b1020',
  // Bleed horizontally past the panel padding so the top border
  // and background run edge-to-edge. Horizontal padding is
  // re-asserted inside so the footer contents stay aligned with
  // the body. The vertical bleed is only downward (into the panel
  // root's 12 px bottom padding) so the darker band touches the
  // bottom panel edge without a body-coloured gap.
  marginLeft: '-14px',
  marginRight: '-14px',
  marginBottom: '-12px',
  paddingLeft: '14px',
  paddingRight: '14px',
  paddingBottom: '12px',
};

//
// Dropdown menu surface (absolutely positioned above its trigger
// button, since the trigger lives in the footer and there isn't
// room below). Dark chrome, soft drop shadow, same border colour
// as the panel so it reads as part of the same component.
//
export const dropdownMenuStyle: JSX.CSSProperties = {
  position: 'absolute',
  bottom: 'calc(100% + 4px)',
  left: '0',
  minWidth: '220px',
  maxHeight: '60vh',
  overflowY: 'auto',
  background: '#0b1020',
  border: '1px solid #1f2937',
  borderRadius: '6px',
  boxShadow: '0 8px 20px rgba(0,0,0,0.4)',
  padding: '4px',
  zIndex: MAXZ,
};

//
// One selectable row inside a dropdown menu. Full-width, left-
// aligned, with a hover state that matches the active-tab shade.
//
export const dropdownItemStyle: JSX.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '6px 10px',
  background: 'transparent',
  color: '#f3f4f6',
  border: 'none',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: '600',
  textAlign: 'left',
  cursor: 'pointer',
  boxSizing: 'border-box',
};

//
// Title + close-button row at the top of the panel. Sits in a
// slightly darker band that bleeds horizontally to the panel edges
// and upward into the root padding, forming a chrome strip above
// the tab bar. paddingBottom replaces marginBottom so the darker
// band extends all the way down to the tab bar — no body-coloured
// gap between the two.
//
export const headerStyle: JSX.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: '#0b1020',
  marginLeft: '-14px',
  marginRight: '-14px',
  marginTop: '-14px',
  paddingLeft: '14px',
  paddingRight: '14px',
  paddingTop: '14px',
  paddingBottom: '10px',
};

export const titleStyle: JSX.CSSProperties = {
  fontWeight: '700',
  fontSize: '14px',
};

//
// Tab bar sitting directly below the panel header. Each tab owns a
// chunk of the panel body; switching tabs swaps out the visible
// content while the header and footer stay pinned. The bar itself
// is a flex row with a bottom border so the active tab's lack of a
// bottom border reads visually as "this tab is connected to the
// content below".
//
export const tabBarStyle: JSX.CSSProperties = {
  display: 'flex',
  gap: '4px',
  borderBottom: '1px solid #1f2937',
  marginBottom: '10px',
  // Same darker shade as the header, so the whole chrome band
  // above the body — title row + tab bar — reads as one piece.
  background: '#0b1020',
  // Extend the underline and background full-width by bleeding
  // past the panel's horizontal padding. The padding is
  // re-asserted inside so the tab buttons stay aligned with
  // everything else.
  marginLeft: '-14px',
  marginRight: '-14px',
  paddingLeft: '14px',
  paddingRight: '14px',
};

//
// A single tab button. Transparent-ish when inactive, elevated to
// the panel background and given a coloured bottom marker when
// active. The `data-badge` slot — rendered as a separate span next
// to the label — shows a count when another tab has non-empty
// state, so the user knows at a glance where their configuration
// lives even when the tab is hidden.
//
export function tabButtonStyle(active: boolean): JSX.CSSProperties {
  return {
    flex: '0 0 auto',
    padding: '6px 12px',
    background: active ? '#1f2937' : 'transparent',
    color: active ? '#f3f4f6' : '#9ca3af',
    border: 'none',
    borderBottom: active ? '2px solid #3b82f6' : '2px solid transparent',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    marginBottom: '-1px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  };
}

//
// Count badge shown next to a tab label when that tab's section
// has non-empty state. Small pill, dimmer than the label itself.
//
export const tabBadgeStyle: JSX.CSSProperties = {
  padding: '0 6px',
  background: '#374151',
  color: '#d1d5db',
  borderRadius: '10px',
  fontSize: '10px',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontWeight: '600',
  minWidth: '14px',
  textAlign: 'center',
  lineHeight: '16px',
};

//
// "N maps detected" line.
//
export const mapInfoStyle: JSX.CSSProperties = {
  opacity: '0.75',
  fontSize: '12px',
  marginBottom: '10px',
};

//
// Start/End captured-view rows.
//
export const viewRowStyle: JSX.CSSProperties = {
  fontSize: '12px',
  opacity: '0.85',
  margin: '6px 0 4px',
};

//
// Duration label + numeric input row.
//
export const durationRowStyle: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  margin: '10px 0 12px',
};

export const durationLabelStyle: JSX.CSSProperties = {
  flex: '1',
};

export const durationInputStyle: JSX.CSSProperties = {
  width: '72px',
  padding: '4px 6px',
  background: '#1f2937',
  color: '#f3f4f6',
  border: '1px solid #374151',
  borderRadius: '4px',
  fontSize: '13px',
  textAlign: 'right',
};

//
// Status text below the Render button.
//
export const statusStyle: JSX.CSSProperties = {
  fontSize: '12px',
  opacity: '0.8',
  marginTop: '10px',
  minHeight: '1.2em',
};

//
// Outer track and inner fill of the progress bar.
//
export const progressWrapStyle: JSX.CSSProperties = {
  height: '6px',
  background: '#1f2937',
  borderRadius: '3px',
  overflow: 'hidden',
  marginTop: '8px',
};

export const progressBarStyle: JSX.CSSProperties = {
  height: '100%',
  width: '0%',
  background: '#3b82f6',
  transition: 'width 120ms ease-out',
};

//
// Timeline container: scrubber row + time axis + four track rows.
// Laid out as a block under the initial-view controls.
//
export const timelineWrapStyle: JSX.CSSProperties = {
  marginTop: '10px',
  border: '1px solid #1f2937',
  borderRadius: '6px',
  background: '#0b1020',
  padding: '8px',
};

//
// A single row of the timeline: a fixed-width label column on the
// left and a relative-positioned track area on the right where
// action blocks live.
//
export const trackRowStyle: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  height: '26px',
  marginBottom: '2px',
};

export const trackLabelStyle: JSX.CSSProperties = {
  width: '56px',
  fontSize: '11px',
  fontWeight: '600',
  color: '#9ca3af',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

export const trackAreaStyle: JSX.CSSProperties = {
  position: 'relative',
  flex: '1',
  height: '22px',
  background: '#0f172a',
  borderRadius: '3px',
  // Clip blocks that extend past the track's right edge. Actions
  // whose end > totalSeconds (created by typing a large duration,
  // or by shrinking totalSeconds after the fact) remain in the
  // data but don't make the panel scroll horizontally.
  overflow: 'hidden',
};

//
// Time axis row: a 56 px label cell (used to print the current
// scrub position) plus the tick strip that sits above the track
// rows.
//
export const axisRowStyle: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: '4px',
};

export const axisLabelStyle: JSX.CSSProperties = {
  width: '56px',
  fontSize: '11px',
  color: '#9ca3af',
  fontVariantNumeric: 'tabular-nums',
};

//
// The tick strip itself. Positioned relative so tick marks (which
// are absolute) anchor to it.
//
export const axisStyle: JSX.CSSProperties = {
  position: 'relative',
  height: '18px',
  color: '#6b7280',
  fontSize: '10px',
};

//
// Scrubber row: a 56 px label cell matching the track labels, plus
// the range input which is sized inline to TRACK_AREA_WIDTH so it
// lines up pixel-for-pixel with the tracks below.
//
export const scrubberRowStyle: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: '4px',
};

export const scrubberLabelStyle: JSX.CSSProperties = {
  width: '56px',
  fontSize: '11px',
  fontWeight: '600',
  color: '#9ca3af',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

export const scrubberInputStyle: JSX.CSSProperties = {
  cursor: 'pointer',
  margin: '0',
  padding: '0',
};

//
// Vertical playhead line rendered inside the track area column to
// mark the current scrub time.
//
export const playheadStyle: JSX.CSSProperties = {
  position: 'absolute',
  top: '-18px',
  bottom: '0',
  width: '1px',
  background: '#f59e0b',
  pointerEvents: 'none',
};

//
// Base style for action blocks. The `background` is overridden per
// type via trackColors below.
//
export const actionBlockStyle: JSX.CSSProperties = {
  position: 'absolute',
  top: '2px',
  bottom: '2px',
  borderRadius: '3px',
  color: '#fff',
  fontSize: '11px',
  fontWeight: '600',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  padding: '0 6px',
  lineHeight: '18px',
  cursor: 'grab',
  userSelect: 'none',
  boxSizing: 'border-box',
};

export const actionBlockSelectedStyle: JSX.CSSProperties = {
  outline: '2px solid #fbbf24',
  outlineOffset: '-1px',
  zIndex: '2',
};

//
// Narrow hit-areas at the left and right edges of each block for
// resize dragging. Absolute, so they sit over the corners of the
// block without affecting its content layout.
//
export const resizeHandleStyle: JSX.CSSProperties = {
  position: 'absolute',
  top: '0',
  bottom: '0',
  width: '6px',
  cursor: 'ew-resize',
};

//
// Per-track colours for the action blocks.
//
export const trackColors: Record<'center' | 'zoom' | 'bearing' | 'pitch', string> = {
  center: '#2563eb',
  zoom: '#059669',
  pitch: '#7c3aed',
  bearing: '#d97706',
};

//
// Compact row layout used by "Add action" and fade controls.
//
export const rowStyle: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginTop: '10px',
};

export const labelSpanStyle: JSX.CSSProperties = {
  fontSize: '12px',
  color: '#d1d5db',
};

export const smallInputStyle: JSX.CSSProperties = {
  width: '72px',
  padding: '4px 6px',
  background: '#1f2937',
  color: '#f3f4f6',
  border: '1px solid #374151',
  borderRadius: '4px',
  fontSize: '13px',
  textAlign: 'right',
};

export const selectStyle: JSX.CSSProperties = {
  padding: '4px 6px',
  background: '#1f2937',
  color: '#f3f4f6',
  border: '1px solid #374151',
  borderRadius: '4px',
  fontSize: '13px',
};

//
// Container for the per-action editor panel.
//
export const editorBoxStyle: JSX.CSSProperties = {
  marginTop: '10px',
  padding: '10px',
  border: '1px solid #1f2937',
  borderRadius: '6px',
  background: '#0b1020',
};

//
// Multi-line input used for CSS-selector lists. Wide enough to see
// a reasonable selector, tall enough to show a few at once.
//
export const textareaStyle: JSX.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  background: '#1f2937',
  color: '#f3f4f6',
  border: '1px solid #374151',
  borderRadius: '4px',
  fontSize: '12px',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  resize: 'vertical',
  boxSizing: 'border-box',
};

//
// Scrollable container for the Mapbox-layer picker list. A bounded
// height so the panel doesn't grow arbitrarily with styles that
// have 100+ layers (common for streets-v12 and similar).
//
export const layerListStyle: JSX.CSSProperties = {
  maxHeight: '220px',
  overflowY: 'auto',
  border: '1px solid #1f2937',
  borderRadius: '4px',
  background: '#0f172a',
  padding: '4px 6px',
};

//
// One row inside the layer list: checkbox, id, type badge.
// Monospaced so long ids line up tidily.
//
export const layerRowStyle: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '2px 0',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: '12px',
  cursor: 'pointer',
};

export const layerTypeBadgeStyle: JSX.CSSProperties = {
  padding: '0 6px',
  background: '#1f2937',
  color: '#9ca3af',
  borderRadius: '10px',
  fontSize: '10px',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

//
// Style overlay for disabled buttons. Spread after buttonStyle(...)
// to dim the button and swap the cursor. Browsers' UA-level
// disabled dimming doesn't show through our inline `background`,
// so without this a disabled Render button still looks clickable.
//
export const disabledButtonStyle: JSX.CSSProperties = {
  opacity: '0.45',
  cursor: 'not-allowed',
};

//
// Button variants. `primary` is the blue Render action; `subtle`
// is the ✕ close button; default is the neutral grey used for
// capture-view actions; `compact` is a smaller variant used in
// inline rows.
//
export function buttonStyle(opts: { primary?: boolean; subtle?: boolean; compact?: boolean; danger?: boolean }): JSX.CSSProperties {
  if (opts.subtle) {
    return {
      background: 'transparent',
      color: '#9ca3af',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      padding: '2px 6px',
    };
  }
  const danger = opts.danger;
  const bg = danger ? '#7f1d1d' : opts.primary ? '#2563eb' : '#374151';
  const border = danger ? '#991b1b' : opts.primary ? '#1d4ed8' : '#4b5563';
  if (opts.compact) {
    return {
      padding: '4px 10px',
      background: bg,
      color: '#fff',
      border: '1px solid ' + border,
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      boxSizing: 'border-box',
    };
  }
  return {
    display: 'block',
    width: '100%',
    padding: '8px 10px',
    background: bg,
    color: '#fff',
    border: '1px solid ' + border,
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    textAlign: 'center',
    boxSizing: 'border-box',
  };
}
